import { WORKSPACE_ID_HEADER } from "@repo/workspace-route";
import { type ReactNode, useCallback, useRef } from "react";
import { useAuth } from "src/features/auth/AuthProvider";
import { ArcTokenContext, type ArcTokenResult } from "./context";

const ARC_TOKEN_ENDPOINT = process.env.NEXT_PUBLIC_ARC_TOKEN_ENDPOINT;
const ARC_APP = "lyra";

export function ArcTokenProvider({
  children,
  workspaceId,
}: {
  children: ReactNode;
  workspaceId: string | null;
}) {
  const tokenRef = useRef<string | null>(null);
  const fetchingPromiseRef = useRef<Promise<ArcTokenResult> | null>(null);
  const { markIpRestricted } = useAuth();

  const fetchToken = useCallback(async (): Promise<ArcTokenResult> => {
    if (fetchingPromiseRef.current) return fetchingPromiseRef.current;
    if (!ARC_TOKEN_ENDPOINT) return { status: "error" };

    const myPromise = (async () => {
      try {
        const res = await fetch(ARC_TOKEN_ENDPOINT, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(workspaceId == null
              ? {}
              : { [WORKSPACE_ID_HEADER]: workspaceId }),
          },
          body: JSON.stringify({ app: ARC_APP }),
        });
        if (!res.ok) {
          if (res.status === 401) return { status: "unauthenticated" } as const;
          const body = await res.json().catch(() => ({}));
          if (res.status === 403 && body.error === "workspace_not_selected")
            return { status: "not_selected" } as const;
          if (res.status === 403 && body.error === "workspace_forbidden")
            return { status: "forbidden" } as const;
          if (res.status === 403 && body.error === "ip_restricted") {
            markIpRestricted();
            return { status: "ip_restricted" } as const;
          }
          return { status: "error" } as const;
        }

        const data = await res.json();
        const newToken = data.token;
        if (typeof newToken !== "string") return { status: "error" } as const;

        tokenRef.current = newToken;
        return { status: "ok", token: newToken } as const;
      } catch {
        return { status: "error" } as const;
      }
    })();

    fetchingPromiseRef.current = myPromise;
    myPromise.finally(() => {
      if (fetchingPromiseRef.current === myPromise) {
        fetchingPromiseRef.current = null;
      }
    });
    return myPromise;
  }, [markIpRestricted, workspaceId]);

  const ensureToken = useCallback(async (): Promise<ArcTokenResult> => {
    if (tokenRef.current) return { status: "ok", token: tokenRef.current };
    return fetchToken();
  }, [fetchToken]);

  const refreshToken = useCallback(async (): Promise<ArcTokenResult> => {
    if (fetchingPromiseRef.current) return fetchingPromiseRef.current;
    tokenRef.current = null;
    return fetchToken();
  }, [fetchToken]);

  return (
    <ArcTokenContext.Provider value={{ ensureToken, refreshToken }}>
      {children}
    </ArcTokenContext.Provider>
  );
}
